/**
 * Balance — partner wallet & ledger.
 *
 * "Refined dark command-center" treatment: atmospheric glow, orchestrated
 * load, a balance hero (available-to-withdraw as the dominant figure) and a
 * type-coded ledger of every account movement.
 */
import type { CSSProperties } from 'react';
import {
  ArrowDownLeft,
  ArrowRight,
  Lock,
  Minus,
  ReceiptText,
  Send,
  Unlock,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Button,
  Card,
  PageHeader,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents, formatDate } from '@/lib/format';
import type { LedgerEntry } from '@/lib/types';

// ─────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────

const rise = (ms: number): CSSProperties => ({
  animation: 'balRise 0.55s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});
const fade = (ms: number): CSSProperties => ({
  animation: 'balFade 0.5s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});

const LEDGER_META: Record<
  LedgerEntry['type'],
  { label: string; tint: string; Icon: LucideIcon }
> = {
  credit: { label: 'Начисление', tint: 'var(--success)', Icon: ArrowDownLeft },
  payout: { label: 'Выплата', tint: 'var(--accent-bright)', Icon: Send },
  hold: { label: 'Холд', tint: 'var(--warning)', Icon: Lock },
  release: { label: 'Разморозка', tint: 'var(--info)', Icon: Unlock },
  debit: { label: 'Списание', tint: 'var(--danger)', Icon: Minus },
};

// ─────────────────────────────────────────────────────────────────────────
//  Balance hero
// ─────────────────────────────────────────────────────────────────────────

function SubStat({ label, value, tint }: { label: string; value: string; tint?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
        {label}
      </div>
      <div
        className="mt-1 text-[18px] font-bold tabular-nums leading-none"
        style={{ color: tint ?? 'var(--text)', letterSpacing: 'var(--tracking-tight)' }}
      >
        {value}
      </div>
    </div>
  );
}

function BalanceHero({
  availableCents,
  holdCents,
  pendingCents,
  totalEarnedCents,
  style,
}: {
  availableCents: number;
  holdCents: number;
  pendingCents: number;
  totalEarnedCents: number;
  style?: CSSProperties;
}) {
  const canWithdraw = availableCents > 0;
  return (
    <Card
      className="overflow-hidden"
      style={{
        ...style,
        borderColor: 'color-mix(in oklch, var(--accent) 24%, var(--border))',
      }}
    >
      <div
        className="relative p-5 md:p-6"
        style={{
          background:
            'radial-gradient(130% 150% at 0% 0%, color-mix(in oklch, var(--accent) 20%, var(--bg-raised)) 0%, var(--bg-raised) 60%)',
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full opacity-60"
          style={{ background: 'radial-gradient(circle, color-mix(in oklch, var(--accent) 30%, transparent), transparent 70%)' }}
        />
        <div className="relative flex flex-wrap items-end justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)]"
                style={{
                  backgroundColor: 'color-mix(in oklch, var(--accent) 16%, transparent)',
                  border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)',
                  color: 'var(--accent-bright)',
                }}
                aria-hidden
              >
                <Wallet size={15} strokeWidth={2} />
              </span>
              <span className="text-[11px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                Доступно к выводу
              </span>
            </div>
            <div
              className="mt-2 text-[32px] md:text-[40px] font-bold tabular-nums leading-none text-[var(--text)]"
              style={{ letterSpacing: 'var(--tracking-tight)' }}
            >
              {formatCents(availableCents)}
            </div>
          </div>
          <Button
            variant={canWithdraw ? 'gradient' : 'secondary'}
            size="md"
            disabled={!canWithdraw}
            onClick={() => {
              window.location.hash = '/payouts';
            }}
          >
            <Send size={15} strokeWidth={2.5} />
            Запросить выплату
            {canWithdraw && <ArrowRight size={15} strokeWidth={2.5} />}
          </Button>
        </div>

        <div className="relative mt-5 grid grid-cols-1 gap-4 border-t border-[var(--border-subtle)] pt-4 sm:grid-cols-3">
          <SubStat label="В холде" value={formatCents(holdCents)} tint="var(--warning)" />
          <SubStat label="Ожидание" value={formatCents(pendingCents)} tint="var(--text-secondary)" />
          <SubStat label="Всего заработано" value={formatCents(totalEarnedCents)} tint="var(--success)" />
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Ledger
// ─────────────────────────────────────────────────────────────────────────

function LedgerSkeleton() {
  return (
    <div className="space-y-3 p-5">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-9 w-9 rounded-[var(--radius-sm)]" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

function LedgerEmpty() {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <div className="relative mb-5">
        <div
          aria-hidden
          className="absolute inset-0 rounded-full blur-2xl"
          style={{ background: 'radial-gradient(circle, color-mix(in oklch, var(--accent) 50%, transparent), transparent 70%)' }}
        />
        <div
          className="relative grid h-[72px] w-[72px] place-items-center rounded-full"
          style={{
            backgroundColor: 'color-mix(in oklch, var(--accent) 14%, var(--bg-raised))',
            border: '1px solid color-mix(in oklch, var(--accent) 32%, transparent)',
            color: 'var(--accent-bright)',
          }}
        >
          <ReceiptText size={28} strokeWidth={2} />
        </div>
      </div>
      <h3 className="text-[18px] font-semibold text-[var(--text)]">Движений по счёту пока нет</h3>
      <p className="mt-2 max-w-[420px] text-[13px] leading-relaxed text-[var(--text-muted)]">
        Здесь появится вся история: начисления комиссий, холды, разморозки и выплаты. Первая
        подтверждённая конверсия откроет ленту движений.
      </p>
    </div>
  );
}

function LedgerTable({ items }: { items: LedgerEntry[] }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Дата</TH>
            <TH>Операция</TH>
            <TH>Описание</TH>
            <TH className="text-right">Сумма</TH>
            <TH className="text-right">Баланс после</TH>
          </TR>
        </THead>
        <TBody>
          {items.map((e, i) => {
            const meta = LEDGER_META[e.type] ?? {
              label: e.type ?? '—',
              tint: 'var(--text-muted)',
              Icon: ReceiptText,
            };
            const Icon = meta.Icon;
            const amount = e.amountCents ?? 0;
            const positive = amount >= 0;
            return (
              <TR key={e.id} style={rise(60 + Math.min(i, 14) * 30)}>
                <TD className="whitespace-nowrap text-[12px] text-[var(--text-muted)] tabular-nums">
                  {formatDate(e.createdAt)}
                </TD>
                <TD>
                  <div className="flex items-center gap-2.5">
                    <span
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
                      style={{
                        backgroundColor: `color-mix(in oklch, ${meta.tint} 14%, transparent)`,
                        border: `1px solid color-mix(in oklch, ${meta.tint} 26%, transparent)`,
                        color: meta.tint,
                      }}
                      aria-hidden
                    >
                      <Icon size={14} strokeWidth={2} />
                    </span>
                    <span className="font-medium text-[var(--text)]">{meta.label}</span>
                  </div>
                </TD>
                <TD className="text-[var(--text-muted)]">{e.description ?? '—'}</TD>
                <TD className="text-right tabular-nums">
                  <span
                    className="font-bold"
                    style={{ color: positive ? 'var(--success)' : 'var(--danger)' }}
                  >
                    {positive ? '+' : '−'}
                    {formatCents(Math.abs(amount))}
                  </span>
                </TD>
                <TD className="text-right tabular-nums font-medium text-[var(--text)]">
                  {formatCents(e.runningBalanceCents)}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

export function BalancePage() {
  const balance = partnerApi.balance.useGet();
  const ledger = partnerApi.balance.useLedger({ pageSize: 50 });

  const b = balance.data;
  const ledgerItems = ledger.data?.items ?? [];
  const ledgerLoading = ledger.isLoading;
  const ledgerEmpty = !ledgerLoading && ledgerItems.length === 0;

  return (
    <div className="relative mx-auto max-w-[1180px]">
      <style>{`
        @keyframes balRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes balFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-120px] -z-10 h-[360px] w-[760px] -translate-x-1/2"
        style={{
          background:
            'radial-gradient(ellipse at center, color-mix(in oklch, var(--accent) 20%, transparent), transparent 68%)',
          filter: 'blur(36px)',
        }}
      />

      <div style={rise(0)}>
        <PageHeader
          eyebrow="Партнёрская программа"
          title="Баланс"
          subtitle="Доступные средства, холды и полная история движения денег."
        />
      </div>

      <div className="mb-6">
        <BalanceHero
          availableCents={b?.availableCents ?? 0}
          holdCents={b?.holdCents ?? 0}
          pendingCents={b?.pendingCents ?? 0}
          totalEarnedCents={b?.totalEarnedCents ?? 0}
          style={rise(70)}
        />
      </div>

      <Card className="overflow-hidden" style={fade(180)}>
        <div
          aria-hidden
          className="h-[2px] w-full"
          style={{
            background:
              'linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 60%, transparent) 30%, color-mix(in oklch, var(--accent) 60%, transparent) 70%, transparent)',
          }}
        />
        <div className="flex items-center gap-2.5 border-b border-[var(--border-subtle)] px-5 py-3.5">
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)]"
            style={{
              backgroundColor: 'color-mix(in oklch, var(--accent) 14%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 26%, transparent)',
              color: 'var(--accent-bright)',
            }}
            aria-hidden
          >
            <ReceiptText size={14} strokeWidth={2} />
          </span>
          <h2 className="text-[15px] font-semibold text-[var(--text)]">История движений</h2>
          {!ledgerLoading && ledgerItems.length > 0 && (
            <span className="rounded-[var(--radius-pill)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--text-muted)]">
              {ledgerItems.length}
            </span>
          )}
        </div>

        {ledgerLoading && <LedgerSkeleton />}
        {ledgerEmpty && <LedgerEmpty />}
        {!ledgerLoading && ledgerItems.length > 0 && <LedgerTable items={ledgerItems} />}
      </Card>
    </div>
  );
}
